import { Injectable, inject } from '@angular/core'
import { HttpClient } from '@angular/common/http'
import { AuthApiService } from 'src/api/auth'
import { compact } from 'lodash-es'
import { environment } from 'src/environments/environment'

@Injectable({
  providedIn: 'root'
})
export class DelightedNpsService {
  private http = inject(HttpClient)
  private authApi = inject(AuthApiService)

  private scriptInjected = false
  private readonly enableWA19462: boolean = false;

  constructor() {
    this.enableWA19462 = environment.WA_19462_ENABLE_DELIGHTED_NPS ?? false;
  }

  /**
   * Initialize the Delighted NPS script injection.
   * Should be called after user login on default pages.
   */
  initialize(): void {
    // Only inject once
    if (this.scriptInjected || !this.enableWA19462) {
      return
    }

    // Set flag immediately to prevent multiple injections
    this.scriptInjected = true;

    // Get current user directly (we're already checking for user in AppComponent)
    const user = this.authApi.currentUser
    if (!user) {
      console.error('Delighted NPS: No user found')
      // Reset flag to allow future attempts
      this.scriptInjected = false;
      return
    }

    this.injectScript(user)
  }

  /**
   * Escape string for safe use in JavaScript
   */
  private escapeJsString(str: string): string {
    return str
      .replace(/\\/g, '\\\\')
      .replace(/"/g, '\\"')
      .replace(/'/g, "\\'")
      .replace(/\n/g, '\\n')
      .replace(/\r/g, '\\r')
      .replace(/\t/g, '\\t')
  }

  /**
   * Inject the Delighted NPS script with user data
   */
  private async injectScript(user: any): Promise<void> {
    try {
      // Fetch the HTML template
      const template = await this.http.get('assets/js/delighted/nps.js', { responseType: 'text' }).toPromise()
      
      if (!template) {
        console.error('Delighted NPS: Failed to load template')
        this.scriptInjected = false;
        return
      }

      // Replace placeholders with user data (properly escaped)
      const userEmail = this.escapeJsString(user.primaryemail || '')
      const userName = this.escapeJsString(compact([user.firstname, user.lastname]).join(' ') || '')
      const userType = this.escapeJsString(user.role || '')

      const scriptContent = template
        .replace(/email: ""/, `email: "${userEmail}"`)
        .replace(/name: ""/, `name: "${userName}"`)
        .replace(/userType: ""/, `userType: "${userType}"`)
        .replace(/createdAt: ""/, `createdAt: "${new Date().toISOString()}"`)

      // Create and inject script element
      const script = document.createElement('script')
      script.type = 'text/javascript'
      script.textContent = scriptContent
      
      // Remove any existing Delighted script
      const existingScript = document.querySelector('script[data-delighted-nps]')
      if (existingScript) {
        existingScript.remove()
      }
      
      script.setAttribute('data-delighted-nps', 'true')
      document.head.appendChild(script)
      
      this.scriptInjected = true
    } catch (error) {
      console.error('Delighted NPS: Failed to inject script', error)
      this.scriptInjected = false;
    }
  }
}
